import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { LuChevronRight, LuRefreshCw, LuSearch, LuShieldAlert, LuUsers } from 'react-icons/lu'

import { analyticsService } from '../../../api/services/analytics.service'
import type {
  RiskClassification,
  SupplierRiskItem,
  SupplierRiskRankingResponse,
} from '../../../api/models/response/supplierRisk'
import { useHasPermission } from '../../../auth/PermissionContext'
import { fonts, mineral } from '../../../theme'
import {
  ActionButton,
  EmptyState,
  FilterBar,
  FilterField,
  KpiGrid,
  KpiTile,
  Notice,
  Panel,
  ReportHeader,
  ReportPage,
  ReportSkeleton,
  ToneBadge,
  VerdictBanner,
} from '../../../components/analytics/ReportKit'
import { TONES, reportAccent, selectStyle, table, type Tone } from '../../../components/analytics/reportTheme'
import { RiskDetailDrawer } from './RiskDetailDrawer'
import { ScoreMeter } from './ScoreMeter'
import {
  CLASS_ACTION,
  CLASS_LABEL,
  CLASS_ORDER,
  CLASS_RANGE,
  CLASS_TONE,
  fmtInt,
  fmtIsoDate,
  fmtPct,
  ruleShort,
  statusTone,
  triggeredPenalties,
} from './riskCopy'

/** Filtro de nivel: un nivel del baremo, solo tolerancia cero, o todos. */
type LevelFilter = 'all' | RiskClassification | 'critical'

/**
 * RANKING DE RIESGO DE PROVEEDORES (req. 2.3) — dimensión documental.
 * Índice 0–100 por proveedor minero (100 = crítico). La barra de distribución filtra
 * la tabla; cada fila abre el desglose de reglas que sumaron, en lenguaje llano.
 */
export default function RiesgoProveedoresPage() {
  const navigate = useNavigate()
  const canOpenProfile = useHasPermission('miners.view')

  const [data, setData] = useState<SupplierRiskRankingResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [level, setLevel] = useState<LevelFilter>('all')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await analyticsService.supplierRiskRanking())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void fetchData() }, [fetchData])

  const items = useMemo(() => data?.items ?? [], [data])
  const summary = data?.summary

  // Puesto en el ranking completo: no cambia al filtrar.
  const rankById = useMemo(() => new Map(items.map((it, i) => [it.supplierId, i + 1])), [items])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return items.filter((it) => {
      if (level === 'critical' && !it.disqualified) return false
      if (level !== 'all' && level !== 'critical' && it.classification !== level) return false
      if (!q) return true
      return it.legalName.toLowerCase().includes(q) || it.ruc.includes(q)
    })
  }, [items, search, level])

  const selected = useMemo(() => items.find((it) => it.supplierId === selectedId) ?? null, [items, selectedId])
  const closeDrawer = useCallback(() => setSelectedId(null), [])
  const openProfile = useCallback((it: SupplierRiskItem) => {
    if (it.companyId) navigate(`/app/proveedores/${it.companyId}/detalle`)
  }, [navigate])

  const counts: Record<RiskClassification, number> = {
    High: summary?.highCount ?? 0,
    Medium: summary?.mediumCount ?? 0,
    Low: summary?.lowCount ?? 0,
  }
  const total = summary?.total ?? 0

  return (
    <ReportPage>
      <ReportHeader
        icon={LuShieldAlert}
        title="Riesgo de Proveedores"
        subtitle="Dimensión documental: índice de 0 a 100 por vigencia de documentos, estado en SUNAT y REINFO, y requisitos de tolerancia cero."
        actions={<ActionButton variant="primary" icon={LuRefreshCw} label="Actualizar" loading={loading} onClick={() => void fetchData()} />}
      />

      {error && <Notice tone="bad">No se pudo cargar el ranking: {error}</Notice>}

      {!data && loading && <ReportSkeleton />}

      {data && total === 0 && (
        <Panel>
          <EmptyState icon={LuUsers} title="No hay proveedores para evaluar"
            hint="El ranking incluye a los proveedores mineros con el registro finalizado." />
        </Panel>
      )}

      {data && summary && total > 0 && (
        <>
          <Verdict counts={counts} total={total} disqualified={summary.disqualifiedCount ?? 0} evaluatedOn={summary.evaluatedOn} />

          <KpiGrid>
            <KpiTile index={0} label="Proveedores evaluados" value={total} format={fmtInt} icon={LuUsers} tone="accent" />
            {CLASS_ORDER.map((c, i) => (
              <KpiTile key={c} index={i + 1} label={`Riesgo ${CLASS_LABEL[c].toLowerCase()}`} value={counts[c]} format={fmtInt}
                tone={CLASS_TONE[c]} hint={`${CLASS_RANGE[c]} pts · ${CLASS_ACTION[c]}`} />
            ))}
          </KpiGrid>

          <Panel index={1} title="Distribución del riesgo"
            subtitle="Toca un nivel para filtrar la tabla; vuelve a tocarlo para quitar el filtro.">
            <DistributionBar counts={counts} total={total} level={level}
              onPick={(c) => setLevel((prev) => (prev === c ? 'all' : c))} />
          </Panel>

          {!summary.premiumRulesEnabled && (
            <Notice tone="info">
              Las reglas premium (actividad CIIU, titular REINFO igual al representante legal y registro de accionistas/UBO)
              no se están evaluando y no suman puntos.
            </Notice>
          )}

          <FilterBar note={`Mostrando ${fmtInt(filtered.length)} de ${fmtInt(total)} proveedores.`}>
            <FilterField label="Buscar" width={{ base: '100%', md: '320px' }}>
              <Box position="relative">
                <Box position="absolute" left="11px" top="50%" transform="translateY(-50%)" color={mineral.ink3} display="flex" pointerEvents="none">
                  <LuSearch size={15} />
                </Box>
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Razón social o RUC" aria-label="Buscar proveedor por razón social o RUC"
                  style={{ ...selectStyle, paddingLeft: 34 }} />
              </Box>
            </FilterField>
            <FilterField label="Nivel" width={{ base: '100%', md: '220px' }}>
              <select style={selectStyle} value={level} onChange={(e) => setLevel(e.target.value as LevelFilter)}>
                <option value="all">Todos los niveles</option>
                {CLASS_ORDER.map((c) => <option key={c} value={c}>Riesgo {CLASS_LABEL[c].toLowerCase()}</option>)}
                <option value="critical">Solo tolerancia cero</option>
              </select>
            </FilterField>
          </FilterBar>

          <Panel index={2} padded={false} title="Ranking" subtitle="De mayor a menor riesgo. Haz clic en un proveedor para ver qué reglas sumaron.">
            {filtered.length === 0 ? (
              <EmptyState icon={LuSearch} title="Ningún proveedor coincide" hint="Prueba con otro nombre o RUC, o quita el filtro de nivel." />
            ) : (
              <Box overflowX="auto">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...table.th, width: 48 }}>#</th>
                      <th style={table.th}>Proveedor</th>
                      <Box as="th" style={table.th} display={{ base: 'none', md: 'table-cell' }}>REINFO</Box>
                      <Box as="th" style={table.th} display={{ base: 'none', md: 'table-cell' }}>RUC (SUNAT)</Box>
                      <th style={table.th}>Puntaje</th>
                      <Box as="th" style={table.th} display={{ base: 'none', lg: 'table-cell' }}>Motivo principal</Box>
                      <th style={{ ...table.th, width: 36 }} aria-label="Abrir detalle" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((it, i) => (
                      <RankingRow key={it.supplierId} item={it} rank={rankById.get(it.supplierId) ?? i + 1} index={i}
                        selected={it.supplierId === selectedId} onOpen={() => setSelectedId(it.supplierId)} />
                    ))}
                  </tbody>
                </table>
              </Box>
            )}
          </Panel>
        </>
      )}

      <RiskDetailDrawer
        item={selected}
        rank={selected ? rankById.get(selected.supplierId) ?? null : null}
        onClose={closeDrawer}
        onOpenProfile={canOpenProfile && selected?.companyId ? openProfile : undefined}
      />
    </ReportPage>
  )
}

/** Veredicto: cuántos proveedores exigen acción, y cuántos por tolerancia cero. */
function Verdict({ counts, total, disqualified, evaluatedOn }: {
  counts: Record<RiskClassification, number>; total: number; disqualified: number; evaluatedOn: string | null
}) {
  const meta = (
    <>
      <Text fontSize="11px" color={mineral.ink3} textTransform="uppercase" letterSpacing="0.06em">Vigencias al</Text>
      <Text fontWeight={700} color={mineral.ink}>{fmtIsoDate(evaluatedOn)}</Text>
    </>
  )
  if (counts.High > 0) {
    return (
      <VerdictBanner tone="bad" meta={meta}
        title={`${fmtInt(counts.High)} de ${fmtInt(total)} ${counts.High === 1 ? 'proveedor está' : 'proveedores están'} en riesgo alto`}
        subtitle={disqualified > 0
          ? `${fmtInt(disqualified)} por una regla de tolerancia cero (RUC no activo, REINFO no vigente o sin IGAFOM vigente). Abre cada uno para ver qué falta.`
          : 'Ninguno por tolerancia cero: suman varias alertas a la vez. Abre cada uno para ver qué falta.'} />
    )
  }
  if (counts.Medium > 0) {
    return (
      <VerdictBanner tone="warn" meta={meta}
        title={`${fmtInt(counts.Medium)} ${counts.Medium === 1 ? 'proveedor requiere' : 'proveedores requieren'} debida diligencia reforzada`}
        subtitle="Ningún proveedor en riesgo alto. Revisa los de riesgo medio antes de su próxima compra." />
    )
  }
  return (
    <VerdictBanner tone="ok" meta={meta} title="Todos los proveedores están en riesgo bajo"
      subtitle="Documentación vigente y estados SUNAT/REINFO en regla." />
  )
}

/** Barra apilada alto/medio/bajo. Cada tramo (y su leyenda) filtra la tabla. */
function DistributionBar({ counts, total, level, onPick }: {
  counts: Record<RiskClassification, number>; total: number; level: LevelFilter; onPick: (c: RiskClassification) => void
}) {
  const active = (c: RiskClassification) => level === 'all' || level === c || (level === 'critical' && c === 'High')
  return (
    <Box>
      <Flex h="38px" borderRadius="12px" overflow="hidden" gap="3px" bg={mineral.surface}>
        {CLASS_ORDER.filter((c) => counts[c] > 0).map((c, i) => {
          const t = TONES[CLASS_TONE[c]]
          const pct = (counts[c] / total) * 100
          return (
            <motion.button key={c} type="button" onClick={() => onPick(c)}
              aria-label={`Filtrar riesgo ${CLASS_LABEL[c].toLowerCase()}: ${counts[c]} proveedores`} aria-pressed={level === c}
              initial={{ width: 0 }} animate={{ width: `${pct}%`, opacity: active(c) ? 1 : 0.35 }}
              transition={{ width: { delay: 0.08 * i, duration: 0.6, ease: [0.16, 1, 0.3, 1] }, opacity: { duration: 0.2 } }}
              whileHover={{ filter: 'brightness(1.06)' }}
              style={{
                background: t.color, color: mineral.surface, border: 'none', cursor: 'pointer', minWidth: 28,
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                fontFamily: fonts.display, fontWeight: 800, fontSize: 14,
              }}>
              {pct >= 8 ? fmtInt(counts[c]) : null}
            </motion.button>
          )
        })}
      </Flex>
      <Flex mt={3} gap={2} wrap="wrap">
        {CLASS_ORDER.map((c) => {
          const tone = CLASS_TONE[c]
          const t = TONES[tone]
          const on = level === c
          return (
            <Flex key={c} as="button" {...{ type: 'button' }} onClick={() => onPick(c)} align="center" gap={2} px={3} py={1.5}
              borderRadius="10px" cursor="pointer" border={`1px solid ${on ? t.color : mineral.line}`} bg={on ? t.soft : mineral.surface}
              transition="all .15s" _hover={{ borderColor: t.color }}>
              <Box w="9px" h="9px" borderRadius="full" bg={t.color} />
              <Text fontSize="13px" color={mineral.ink2}>Riesgo {CLASS_LABEL[c].toLowerCase()}</Text>
              <Text fontSize="13px" fontWeight={700} color={t.deep}>{fmtInt(counts[c])}</Text>
              <Text fontSize="12px" color={mineral.ink3}>· {fmtPct((counts[c] / total) * 100)}</Text>
            </Flex>
          )
        })}
      </Flex>
    </Box>
  )
}

function StatusCell({ value, tone }: { value: string | null; tone: Tone }) {
  if (!value) return <Text as="span" color={mineral.ink4}>—</Text>
  return (
    <Flex align="center" gap={1.5} minW={0} maxW="170px" title={value}>
      <Box w="7px" h="7px" borderRadius="full" bg={TONES[tone].color} flexShrink={0} />
      <Text fontSize="12.5px" color={tone === 'bad' ? TONES.bad.deep : mineral.ink2} fontWeight={tone === 'bad' ? 600 : 400} truncate>
        {value}
      </Text>
    </Flex>
  )
}

function RankingRow({ item, rank, index, selected, onOpen }: {
  item: SupplierRiskItem; rank: number; index: number; selected: boolean; onOpen: () => void
}) {
  const tone = CLASS_TONE[item.classification]
  const fired = triggeredPenalties(item)
  const main = fired[0]
  return (
    <motion.tr
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.02, 0.3), duration: 0.2 }}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } }}
      tabIndex={0}
      aria-label={`${item.legalName}: ${item.score} puntos, riesgo ${CLASS_LABEL[item.classification].toLowerCase()}`}
      style={{ cursor: 'pointer', background: selected ? reportAccent.soft : undefined }}
      whileHover={{ backgroundColor: mineral.surfaceAlt }}
    >
      <td style={{ ...table.td, fontFamily: fonts.mono, color: mineral.ink4, fontSize: 12 }}>{fmtInt(rank)}</td>
      <td style={{ ...table.td, minWidth: 180 }}>
        <Text fontWeight={600} color={mineral.ink} lineClamp={1}>{item.legalName || '—'}</Text>
        <Text fontSize="11.5px" fontFamily={fonts.mono} color={mineral.ink3}>{item.ruc || '—'}</Text>
      </td>
      <Box as="td" style={table.td} display={{ base: 'none', md: 'table-cell' }}>
        <StatusCell value={item.reinfoStatus} tone={statusTone(item, 'REINFO_ESTADO')} />
      </Box>
      <Box as="td" style={table.td} display={{ base: 'none', md: 'table-cell' }}>
        <StatusCell value={item.taxpayerStatus} tone={statusTone(item, 'RUC_ESTADO')} />
      </Box>
      <td style={table.td}>
        <Flex align="center" gap={3}>
          <ScoreMeter score={item.score} tone={tone} />
          <Box display={{ base: 'none', sm: 'block' }}><ToneBadge tone={tone}>{CLASS_LABEL[item.classification]}</ToneBadge></Box>
        </Flex>
      </td>
      <Box as="td" style={table.td} display={{ base: 'none', lg: 'table-cell' }}>
        {main ? (
          <Flex align="center" gap={1.5} wrap="wrap">
            <Text fontSize="12.5px" color={main.critical ? TONES.bad.deep : mineral.ink2} fontWeight={main.critical ? 600 : 400}>
              {ruleShort(main)}
            </Text>
            {fired.length > 1 && <Text fontSize="11.5px" color={mineral.ink3}>+{fmtInt(fired.length - 1)} más</Text>}
          </Flex>
        ) : (
          <Text fontSize="12.5px" color={TONES.ok.deep}>Todo en regla</Text>
        )}
      </Box>
      <td style={{ ...table.td, color: mineral.ink4 }}><LuChevronRight size={16} /></td>
    </motion.tr>
  )
}
